import { useState, useEffect } from "react";
import { 
  Search, Target, Trophy, Users, MapPin, Clock, ArrowRight, 
  HandHelping, ChevronDown, Sparkles, Plus, X, CheckCircle, AlertCircle, Loader2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { SEO } from "../components/SEO";
import { NavbarLight } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  open: { label: "Open", color: "bg-[#3B82F6]", icon: Target },
  in_progress: { label: "In Progress", color: "bg-[#D4A017] text-[#1A1915]", icon: Users },
  completed: { label: "Completed", color: "bg-[#2C4A3B]", icon: Trophy },
};

// Community Wins Carousel
const CommunityWins = ({ wins }) => {
  if (!wins.length) return null;

  return (
    <section className="bg-[#1A2F23] py-12 px-6 md:px-12" data-testid="community-wins-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Trophy className="w-6 h-6 text-[#D4A017]" />
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white">
            Community Wins
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {wins.slice(0, 6).map((win) => (
            <div
              key={win.id}
              className="bg-gradient-to-br from-[#2C4A3B] to-[#1A2F23] border border-[#D4A017]/20 rounded-xl p-5 relative overflow-hidden"
              data-testid={`win-card-${win.id}`}
            >
              <div className="absolute top-3 right-3">
                <Trophy className="w-8 h-8 text-[#D4A017]/20" />
              </div>
              <Badge className="bg-[#D4A017]/20 text-[#D4A017] text-[10px] mb-3">{win.category}</Badge>
              <h3 className="font-semibold text-white text-lg mb-2 pr-8">{win.title}</h3>
              <p className="text-white/60 text-sm line-clamp-2 mb-3">{win.description}</p>
              {win.completion_note && (
                <p className="text-[#14B8A6] text-sm italic border-l-2 border-[#14B8A6]/40 pl-3">
                  "{win.completion_note}"
                </p>
              )}
              <div className="flex items-center gap-3 mt-3 text-white/40 text-xs">
                {win.poster_name && <span>by {win.poster_name}</span>}
                {win.town && (
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{win.town}</span>
                )}
                {(win.helpers_public || []).length > 0 && (
                  <span className="flex items-center gap-1">
                    <HandHelping className="w-3 h-3" />{win.helpers_public.length} helped
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Help Offer Modal
const HelpModal = ({ isOpen, onClose, opportunity }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", skills: [], is_public: false });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/opportunities/${opportunity.id}/help`, form);
      toast.success("Thank you for offering to help!");
      onClose();
      setForm({ name: "", email: "", phone: "", message: "", skills: [], is_public: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#EFEDE5]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#1A1915]">
            I Can Help: {opportunity?.title}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#1A1915] font-medium">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 bg-white border-[#D1CFC5]" data-testid="help-name" />
            </div>
            <div>
              <Label className="text-[#1A1915] font-medium">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1 bg-white border-[#D1CFC5]" data-testid="help-email" />
            </div>
          </div>
          <div>
            <Label className="text-[#1A1915] font-medium">Phone (optional)</Label>
            <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="mt-1 bg-white border-[#D1CFC5]" placeholder="(250) 555-1234" data-testid="help-phone" />
          </div>
          <div>
            <Label className="text-[#1A1915] font-medium">Message</Label>
            <Textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="mt-1 bg-white border-[#D1CFC5]" placeholder="How can you help? What relevant experience do you have?" rows={3} data-testid="help-message" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer bg-[#F7F6F2] border border-[#D1CFC5] rounded-lg p-3" data-testid="help-public-toggle">
            <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({...form, is_public: e.target.checked})} className="w-4 h-4 accent-[#2C4A3B]" />
            <div>
              <p className="text-sm font-medium text-[#1A1915]">Show my name publicly</p>
              <p className="text-xs text-[#5C5A52]">Your contact info stays private. Only your name and skills will be visible.</p>
            </div>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold" data-testid="help-submit-btn">
              {submitting ? "Sending..." : "Offer My Help"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Post Opportunity Modal
const PostModal = ({ isOpen, onClose, categories, onCreated }) => {
  const [form, setForm] = useState({
    title: "", description: "", category: "", town: "", skills_needed: [],
    people_needed: "", deadline: "", poster_email: "", suggested_category: ""
  });
  const [skillInput, setSkillInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const towns = ["Invermere", "Radium Hot Springs", "Fairmont Hot Springs", "Panorama", "Windermere", "Canal Flats", "Edgewater", "Valley-wide"];

  const addSkill = () => {
    if (skillInput.trim() && !form.skills_needed.includes(skillInput.trim())) {
      setForm({...form, skills_needed: [...form.skills_needed, skillInput.trim()]});
      setSkillInput("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.category || !form.poster_email) {
      toast.error("Please fill in title, description, category, and your email");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        people_needed: form.people_needed ? parseInt(form.people_needed) : null,
        deadline: form.deadline || null,
        suggested_category: form.suggested_category || null
      };
      await axios.post(`${API}/opportunities`, payload);
      toast.success("Posted! Your opportunity will appear once approved by an admin.");
      onClose();
      onCreated();
      setForm({ title: "", description: "", category: "", town: "", skills_needed: [], people_needed: "", deadline: "", poster_email: "", suggested_category: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#EFEDE5]">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#1A1915]">Post an Opportunity or Challenge</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label className="text-[#1A1915] font-medium">Your Email * (must be a registered volunteer)</Label>
            <Input value={form.poster_email} onChange={(e) => setForm({...form, poster_email: e.target.value})} type="email" className="mt-1 bg-white border-[#D1CFC5]" placeholder="your@email.com" data-testid="post-email" />
          </div>
          <div>
            <Label className="text-[#1A1915] font-medium">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1 bg-white border-[#D1CFC5]" placeholder="What needs to happen?" data-testid="post-title" />
          </div>
          <div>
            <Label className="text-[#1A1915] font-medium">Description *</Label>
            <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 bg-white border-[#D1CFC5]" placeholder="Describe the opportunity, what help is needed, and what success looks like..." rows={4} data-testid="post-description" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[#1A1915] font-medium">Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger className="mt-1 bg-white border-[#D1CFC5]" data-testid="post-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#1A1915] font-medium">Town</Label>
              <Select value={form.town} onValueChange={(v) => setForm({...form, town: v})}>
                <SelectTrigger className="mt-1 bg-white border-[#D1CFC5]" data-testid="post-town">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  {towns.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[#1A1915] font-medium">People Needed</Label>
              <Input type="number" value={form.people_needed} onChange={(e) => setForm({...form, people_needed: e.target.value})} className="mt-1 bg-white border-[#D1CFC5]" placeholder="e.g. 5" data-testid="post-people" />
            </div>
            <div>
              <Label className="text-[#1A1915] font-medium">Deadline</Label>
              <Input type="date" value={form.deadline} onChange={(e) => setForm({...form, deadline: e.target.value})} className="mt-1 bg-white border-[#D1CFC5]" data-testid="post-deadline" />
            </div>
          </div>
          {/* Skills Needed */}
          <div>
            <Label className="text-[#1A1915] font-medium">Skills Needed</Label>
            <div className="flex gap-2 mt-1">
              <Input value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())} className="bg-white border-[#D1CFC5]" placeholder="Type a skill and press Enter" data-testid="post-skill-input" />
              <Button type="button" variant="outline" onClick={addSkill}>Add</Button>
            </div>
            {form.skills_needed.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.skills_needed.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 bg-[#2C4A3B]/10 text-[#2C4A3B] text-xs px-2.5 py-1 rounded-full">
                    {s}
                    <button type="button" onClick={() => setForm({...form, skills_needed: form.skills_needed.filter(sk => sk !== s)})} className="hover:text-red-500">&times;</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Suggest a Category */}
          <div>
            <Label className="text-[#1A1915] font-medium">Suggest a New Category (optional)</Label>
            <Input value={form.suggested_category} onChange={(e) => setForm({...form, suggested_category: e.target.value})} className="mt-1 bg-white border-[#D1CFC5]" placeholder="If none of the above fit, suggest one..." data-testid="post-suggest-category" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold px-8" data-testid="post-submit-btn">
              {submitting ? "Posting..." : "Submit for Approval"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Opportunity Card
const OpportunityCard = ({ opp, onHelp }) => {
  const config = statusConfig[opp.status] || statusConfig.open;
  const StatusIcon = config.icon;

  return (
    <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-5 hover:border-[#2C4A3B]/30 transition-colors" data-testid={`opp-card-${opp.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <Badge className={`${config.color} text-xs`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {config.label}
            </Badge>
            <Badge className="bg-[#F7F6F2] text-[#5C5A52] text-xs">{opp.category}</Badge>
          </div>
          <h3 className="font-heading text-lg font-semibold text-[#1A1915]">{opp.title}</h3>
        </div>
      </div>

      <p className="text-[#5C5A52] text-sm mb-4 line-clamp-3">{opp.description}</p>

      {/* Meta */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-[#5C5A52] mb-4">
        {opp.town && (
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{opp.town}</span>
        )}
        {opp.poster_name && (
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />by {opp.poster_name}</span>
        )}
        {opp.deadline && (
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />by {opp.deadline}</span>
        )}
        {opp.people_needed && (
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{opp.people_needed} people needed</span>
        )}
      </div>

      {/* Skills */}
      {opp.skills_needed?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {opp.skills_needed.map((s) => (
            <span key={s} className="text-[10px] bg-[#2C4A3B]/8 text-[#2C4A3B] px-2 py-0.5 rounded-full">{s}</span>
          ))}
        </div>
      )}

      {/* Public helpers */}
      {opp.helpers_public?.length > 0 && (
        <div className="flex items-center gap-1 text-xs text-[#2C4A3B] mb-4">
          <HandHelping className="w-3 h-3" />
          <span>{opp.helpers_public.length} helping: {opp.helpers_public.map(h => h.name).join(", ")}</span>
        </div>
      )}

      {/* Action */}
      {opp.status !== "completed" && (
        <Button
          onClick={() => onHelp(opp)}
          className="w-full bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold"
          data-testid={`help-btn-${opp.id}`}
        >
          <HandHelping className="w-4 h-4 mr-2" />
          I Can Help
        </Button>
      )}

      {opp.status === "completed" && opp.completion_note && (
        <div className="bg-[#2C4A3B]/5 border border-[#2C4A3B]/20 rounded-lg p-3">
          <p className="text-sm text-[#2C4A3B] italic flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {opp.completion_note}
          </p>
        </div>
      )}
    </div>
  );
};

const Opportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [wins, setWins] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: "", status: "", search: "" });
  const [showPostModal, setShowPostModal] = useState(false);
  const [helpTarget, setHelpTarget] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only

  useEffect(() => {
    fetchOpportunities();
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps -- fetchOpportunities is stable

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catRes, winsRes] = await Promise.all([
        axios.get(`${API}/opportunities/categories`),
        axios.get(`${API}/opportunities/wins`)
      ]);
      setCategories(catRes.data.categories);
      setWins(winsRes.data);
    } catch (e) {
      console.error("Error:", e);
    }
    await fetchOpportunities();
    setLoading(false);
  };

  const fetchOpportunities = async () => {
    try {
      const params = {};
      if (filter.category) params.category = filter.category;
      if (filter.status) params.status = filter.status;
      if (filter.search) params.search = filter.search;
      const res = await axios.get(`${API}/opportunities`, { params });
      setOpportunities(res.data);
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const activeOpps = opportunities.filter(o => o.status !== "completed");
  const openCount = opportunities.filter(o => o.status === "open").length;
  const inProgressCount = opportunities.filter(o => o.status === "in_progress").length;

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <SEO
        title="Opportunities & Challenges | ekvalleys.com"
        description="Collaborate with the East Kootenay community. Post projects, offer help, and celebrate wins together."
        keywords="East Kootenay opportunities, community projects, volunteer, EK Valleys, collaboration"
      />
      <NavbarLight />

      {/* Hero */}
      <section className="bg-[#1A2F23] py-14 md:py-18 px-6 md:px-12 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-center mb-5">
            <div className="w-16 h-16 rounded-full bg-[#D4A017]/20 border-2 border-[#D4A017]/40 flex items-center justify-center">
              <Target className="w-8 h-8 text-[#D4A017]" />
            </div>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="opportunities-page-title">
            Opportunities & Challenges
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-6">
            A place to collaborate, lend a hand, and celebrate our community's wins. Post a project or goal — or step up to help someone else achieve theirs.
          </p>
          <Button
            onClick={() => setShowPostModal(true)}
            className="bg-[#D4A017] hover:bg-[#E8B731] text-[#1A1915] font-semibold px-8 py-3 text-lg"
            data-testid="post-opportunity-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Post an Opportunity
          </Button>
        </div>
      </section>

      {/* Community Wins */}
      <CommunityWins wins={wins} />

      {/* Filters */}
      <section className="py-8 px-6 md:px-12 border-b border-[#D1CFC5]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5A52]" />
              <Input
                value={filter.search}
                onChange={(e) => setFilter({...filter, search: e.target.value})}
                className="pl-10 bg-white border-[#D1CFC5]"
                placeholder="Search opportunities..."
                data-testid="opp-search"
              />
            </div>
            <Select value={filter.category} onValueChange={(v) => setFilter({...filter, category: v === "all" ? "" : v})}>
              <SelectTrigger className="w-full md:w-48 bg-white border-[#D1CFC5]" data-testid="opp-category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filter.status} onValueChange={(v) => setFilter({...filter, status: v === "all" ? "" : v})}>
              <SelectTrigger className="w-full md:w-40 bg-white border-[#D1CFC5]" data-testid="opp-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Quick Stats */}
          <div className="flex items-center gap-6 mt-4 text-sm text-[#5C5A52]">
            <span className="flex items-center gap-1.5"><Target className="w-4 h-4 text-[#3B82F6]" />{openCount} Open</span>
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-[#D4A017]" />{inProgressCount} In Progress</span>
            <span className="flex items-center gap-1.5"><Trophy className="w-4 h-4 text-[#2C4A3B]" />{wins.length} Completed</span>
          </div>
        </div>
      </section>

      {/* Opportunities List */}
      <section className="py-10 px-6 md:px-12" data-testid="opportunities-list">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2C4A3B] mx-auto mb-3" />
              <p className="text-[#5C5A52]">Loading opportunities...</p>
            </div>
          ) : activeOpps.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {activeOpps.map((opp) => (
                <OpportunityCard key={opp.id} opp={opp} onHelp={setHelpTarget} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Target className="w-14 h-14 text-[#D1CFC5] mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-[#1A1915] mb-2">
                {filter.search || filter.category || filter.status ? "No matching opportunities" : "No opportunities posted yet"}
              </h3>
              <p className="text-[#5C5A52] mb-6">
                {filter.search || filter.category || filter.status
                  ? "Try adjusting your filters."
                  : "Be the first to post a community project or challenge!"}
              </p>
              {!(filter.search || filter.category || filter.status) && (
                <Button
                  onClick={() => setShowPostModal(true)}
                  className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white font-semibold"
                  data-testid="post-first-opp-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Post the First One
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Post Modal */}
      <PostModal
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
        categories={categories}
        onCreated={fetchAll}
      />

      {/* Help Modal */}
      {helpTarget && (
        <HelpModal
          isOpen={!!helpTarget}
          onClose={() => setHelpTarget(null)}
          opportunity={helpTarget}
        />
      )}

      <Footer />
    </div>
  );
};

export default Opportunities;
